import React, { useContext, useState, useEffect } from 'react';
import { MessageCircle, Bell, HelpCircle, Crown } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { ChatPopupContext } from '@/components/messaging/FacebookStyleChat/ChatPopupManager';
import { useMediaQuery } from '@/hooks/use-media-query';
import { NotificationDropdown } from '@/components/notifications/NotificationDropdown';
import { useNotificationCount } from '@/hooks/use-notifications';
import { cn } from '@/lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '@/integrations/supabase/client';
import { useSubscription } from '@/hooks/use-subscription';

interface HeaderActionsProps {
  isMobileSidebar?: boolean;
}

export const HeaderActions: React.FC<HeaderActionsProps> = ({ isMobileSidebar = false }) => {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [isUserLoggedIn, setIsUserLoggedIn] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);

  // Try to use the context, but don't throw if it's not available
  const chatContext = useContext(ChatPopupContext);
  const toggleChatList = chatContext?.toggleChatList || (() => {});
  const totalUnreadCount = chatContext?.totalUnreadCount || 0;

  // Get notification unread count
  const notificationUnreadCount = useNotificationCount();

  // Get user subscription tier
  const { userTier, isPro } = useSubscription(userId || undefined);

  // Check if user is logged in using Supabase auth
  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setIsUserLoggedIn(!!session);
      setUserId(session?.user?.id || null);
    };

    checkAuth();

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setIsUserLoggedIn(!!session);
      setUserId(session?.user?.id || null);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Function to open admin support chat
  const openSupportChat = async () => {
    if (!chatContext) return;

    try {
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (!currentUser) {
        console.error('No user logged in');
        return;
      }

      // Get current user's entity ID based on their role
      let currentEntityId: string | null = null;
      let currentRole: 'filmmaker' | 'client' = 'filmmaker';

      // Check if user is filmmaker
      const { data: filmmaker } = await supabase
        .from('filmmaker')
        .select('filmmaker_id')
        .eq('user_id', currentUser.id)
        .single();

      if (filmmaker) {
        currentEntityId = filmmaker.filmmaker_id;
        currentRole = 'filmmaker';
      } else {
        // Check if user is client
        const { data: client } = await supabase
          .from('client')
          .select('client_id')
          .eq('user_id', currentUser.id)
          .single();

        if (client) {
          currentEntityId = client.client_id;
          currentRole = 'client';
        }
      }

      if (!currentEntityId) {
        console.error('Could not find entity ID for current user');
        return;
      }

      // Admin setup matching the existing admin messaging system
      const ADMIN_CLIENT_ID = '7c5377d6-727d-4c6e-bbcb-4b4c34009098'; // Admin's client ID for messaging
      const ADMIN_NAME = 'Find My Filmmaker Admin';
      const ADMIN_LOGO = 'https://vftrlkabvhbpieuoqhzs.supabase.co/storage/v1/object/public/logo/fmf-mobile.png';

      // Create proper room name based on user role
      let roomName: string;
      if (currentRole === 'filmmaker') {
        // Filmmaker messaging admin (client)
        roomName = `chat_client_${ADMIN_CLIENT_ID}_filmmaker_${currentEntityId}`;
      } else {
        // Client messaging admin - treat admin as filmmaker for room structure
        roomName = `chat_client_${currentEntityId}_filmmaker_${ADMIN_CLIENT_ID}`;
      }

      // Open chat with admin using proper room name format
      chatContext.openChat({
        id: roomName, // Use proper room name format
        name: ADMIN_NAME,
        avatar: ADMIN_LOGO,
        role: 'client' as any, // Admin uses client role for messaging
        isMinimized: false,
        unreadCount: 0
      });
    } catch (error) {
      console.error('Error opening support chat:', error);
    }
  };
  
  // Close notification dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (
        isNotificationOpen &&
        !target.closest('.notification-dropdown') &&
        !target.closest('.notification-toggle-button')
      ) {
        setIsNotificationOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isNotificationOpen]);

  // For mobile integration into sidebar header
  if (isMobileSidebar) {
    return (
      <>
        <div className="flex items-center gap-2">
          {/* Chat Icon - only show if context is available */}
          {chatContext && (
            <button
              onClick={toggleChatList}
              className="relative p-2 text-neutral-600 dark:text-neutral-400 hover:text-primary transition-colors"
            >
              <MessageCircle className="h-5 w-5" />
              <AnimatePresence>
                {totalUnreadCount > 0 && (
                  <motion.div
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0, opacity: 0 }}
                    transition={{ 
                      type: "spring", 
                      stiffness: 500, 
                      damping: 25,
                      duration: 0.2 
                    }}
                    className="absolute -top-1 -right-1"
                  >
                    <Badge
                      variant="destructive"
                      className="h-4 min-w-[16px] p-0 flex items-center justify-center text-[10px]"
                    >
                      {totalUnreadCount > 99 ? '99+' : totalUnreadCount}
                    </Badge>
                  </motion.div>
                )}
              </AnimatePresence>
            </button>
          )}

          {/* Notification Button - Mobile Sidebar */}
          <button
            onClick={() => setIsNotificationOpen(!isNotificationOpen)}
            className="relative p-2 text-neutral-600 dark:text-neutral-400 hover:text-primary transition-colors notification-toggle-button"
          >
            <Bell className="h-5 w-5" />
            <AnimatePresence>
              {notificationUnreadCount > 0 && (
                <motion.div
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0, opacity: 0 }}
                  transition={{
                    type: "spring",
                    stiffness: 500,
                    damping: 25,
                    duration: 0.2
                  }}
                  className="absolute -top-1 -right-1"
                >
                  <Badge
                    variant="destructive"
                    className="h-4 min-w-[16px] p-0 flex items-center justify-center text-[10px]"
                  >
                    {notificationUnreadCount > 99 ? '99+' : notificationUnreadCount}
                  </Badge>
                </motion.div>
              )}
            </AnimatePresence>
          </button>

          {/* Support Chat Button - Mobile Sidebar (only when logged in) */}
          {isUserLoggedIn && chatContext && (
            <button
              onClick={openSupportChat}
              className="relative p-2 text-neutral-600 dark:text-neutral-400 hover:text-primary transition-colors"
              title={isPro ? "Priority Support" : "Help & Support"}
            >
              <HelpCircle className="h-5 w-5" />
              {isPro && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="absolute -top-0.5 -right-0.5"
                >
                  <Crown className="h-3 w-3 text-yellow-500 fill-yellow-500" />
                </motion.div>
              )}
            </button>
          )}
        </div>
        
        {/* Mobile Notification Dropdown - Bottom Sheet */}
        <AnimatePresence>
          {isNotificationOpen && (
            <>
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/50 z-40"
                onClick={() => setIsNotificationOpen(false)}
              />
              <motion.div
                initial={{ y: "100%" }}
                animate={{ y: 0 }}
                exit={{ y: "100%" }}
                transition={{
                  type: "spring",
                  damping: 25,
                  stiffness: 300
                }}
                className="fixed bottom-0 left-0 right-0 h-[70vh] bg-background rounded-t-2xl shadow-2xl z-50 notification-dropdown"
              >
                <NotificationDropdown 
                  isOpen={isNotificationOpen} 
                  onClose={() => setIsNotificationOpen(false)} 
                />
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </>
    );
  }

  // For regular mobile (not in sidebar)
  if (isMobile && !isMobileSidebar) {
    return null; // Mobile users will use the header icons from MobileHeaderIcons
  }

  // Always render, but handle missing context gracefully
  return (
    <div className="fixed top-6 right-6 z-50 flex items-center gap-3 header-actions-container">
      {/* Support Chat Button (only when logged in) - Place first for better visibility */}
      {isUserLoggedIn && chatContext && (
        <button
          onClick={openSupportChat}
          className="relative w-12 h-12 bg-primary/10 hover:bg-primary/20 rounded-full flex items-center justify-center transition-all duration-300 shadow-lg hover:shadow-xl group"
          title={isPro ? "Priority Support - Pro Member" : "Chat with Support"}
        >
          <HelpCircle className="h-5 w-5 text-primary group-hover:scale-110 transition-transform" />
          {isPro && (
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 15 }}
              className="absolute -top-1 -right-1"
            >
              <div className="w-5 h-5 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center shadow-lg">
                <Crown className="h-3 w-3 text-white" />
              </div>
            </motion.div>
          )}
        </button>
      )}

      {/* Chat Button */}
      {chatContext && (
        <button
          onClick={toggleChatList}
          className="chat-toggle-button relative w-12 h-12 bg-primary/10 hover:bg-primary/20 rounded-full flex items-center justify-center transition-all duration-300 shadow-lg hover:shadow-xl group"
        >
        <MessageCircle className="h-5 w-5 text-primary group-hover:scale-110 transition-transform" />
        <AnimatePresence>
          {totalUnreadCount > 0 && (
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0, opacity: 0 }}
              transition={{
                type: "spring",
                stiffness: 500,
                damping: 25,
                duration: 0.2
              }}
              className="absolute -top-1 -right-1"
            >
              <Badge
                variant="destructive"
                className="h-5 min-w-[20px] p-0 flex items-center justify-center text-xs"
              >
                {totalUnreadCount > 99 ? '99+' : totalUnreadCount}
              </Badge>
            </motion.div>
          )}
        </AnimatePresence>
      </button>
      )}

      {/* Notification Button */}
      <button
        onClick={() => setIsNotificationOpen(!isNotificationOpen)}
        className="notification-toggle-button relative w-12 h-12 bg-primary/10 hover:bg-primary/20 rounded-full flex items-center justify-center transition-all duration-300 shadow-lg hover:shadow-xl group"
      >
        <Bell className="h-5 w-5 text-primary group-hover:scale-110 transition-transform" />
        <AnimatePresence>
          {notificationUnreadCount > 0 && (
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0, opacity: 0 }}
              transition={{
                type: "spring",
                stiffness: 500,
                damping: 25,
                duration: 0.2
              }}
              className="absolute -top-1 -right-1"
            >
              <Badge
                variant="destructive"
                className="h-5 min-w-[20px] p-0 flex items-center justify-center text-xs"
              >
                {notificationUnreadCount > 99 ? '99+' : notificationUnreadCount}
              </Badge>
            </motion.div>
          )}
        </AnimatePresence>
      </button>

      
      {/* Notification Dropdown */}
      <AnimatePresence>
        {isNotificationOpen && (
          <>
            {/* Backdrop for mobile */}
            {isMobile && (
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/50 z-40"
                onClick={() => setIsNotificationOpen(false)}
              />
            )}
            <motion.div
              initial={
                isMobile 
                  ? { y: "100%" }
                  : { opacity: 0, y: -10 }
              }
              animate={
                isMobile 
                  ? { y: 0 }
                  : { opacity: 1, y: 0 }
              }
              exit={
                isMobile 
                  ? { y: "100%" }
                  : { opacity: 0, scale: 0.95, y: -10 }
              }
              transition={{
                type: "spring",
                damping: 25,
                stiffness: 300
              }}
              className={cn(
                "fixed z-50 notification-dropdown",
                isMobile
                  ? "bottom-0 left-0 right-0 h-[85vh] bg-background rounded-t-2xl shadow-2xl" // Taller bottom sheet on mobile to fit in screen
                  : "top-20 right-6 w-[400px] h-[600px] bg-background border rounded-lg shadow-lg"
              )}
            >
              <NotificationDropdown 
                isOpen={isNotificationOpen} 
                onClose={() => setIsNotificationOpen(false)} 
              />
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

